import type { AutoChecklist, ChecklistStap } from '@fleet/shared';
import { Panel } from '../../components/ui/Panel';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Voortgang } from '../../components/ui/Voortgang';

const MONO = "'Roboto Mono', monospace";
const GEDAAN = '#2A7050';

/**
 * Where a step gets done, as a click.
 *
 * The whole step and not just its `doel`, because the two places that render this
 * list answer the question differently. The dashboard sends you to the car; the car's
 * own page already *is* the car, so there `doel: 'auto'` has to become "scroll to the
 * card that holds this field" — and which card that is follows from `stap.key`.
 */
export type OpStapKlik = (stap: ChecklistStap, kenteken: string) => void;

/**
 * What still has to happen for one new car, as one list.
 *
 * Every step carries a box, so the card reads as the checklist it is. Two kinds of
 * box, though, and the difference is deliberate:
 *
 * - **Tickable** — MoveIZI, the registration, the mail to bookkeeping. These happen
 *   outside the app, so a tick is the only state there is.
 * - **Greyed out** — the other seven. They close when the data is there: a policy
 *   number filled, a pass linked, a rate entered. Clicking the label takes you to
 *   where that happens — see `OpStapKlik`. Leaving them tickable would let somebody
 *   mark insurance done while the field stays empty, and the card would then hide a
 *   gap instead of showing one.
 *
 * Done steps sink to the bottom, so what is still open is always the top of the list.
 */
function StapRegel({
  stap,
  kenteken,
  onNavigate,
  onToggle,
}: {
  stap: ChecklistStap;
  kenteken: string;
  onNavigate: OpStapKlik;
  onToggle: (stap: ChecklistStap) => void;
}) {
  const handmatig = stap.soort === 'handmatig';
  const mail = stap.doel.soort === 'mailto' ? stap.doel : null;
  // Een afgeleide stap is geen dood label: het is de kortste weg naar de plek waar
  // het veld staat dat hem dichtzet.
  const naartoe = !handmatig && stap.doel.soort !== 'geen';
  const geblokkeerd = Boolean(stap.blokkade);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '18px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: 10,
        padding: '5px 0',
        fontSize: 12.5,
      }}
    >
      <input
        type="checkbox"
        checked={stap.gedaan}
        disabled={!handmatig}
        readOnly={!handmatig}
        onChange={handmatig ? () => onToggle(stap) : undefined}
        aria-label={stap.label}
        title={
          handmatig
            ? stap.label
            : stap.gedaan
              ? 'Staat in de app, dus afgevinkt'
              : 'Vinkt zichzelf af zodra het in de app staat'
        }
        style={{
          width: 14,
          height: 14,
          accentColor: GEDAAN,
          cursor: handmatig ? 'pointer' : 'default',
        }}
      />

      <span
        onClick={naartoe ? () => onNavigate(stap, kenteken) : undefined}
        // De bevinding staat achter de stap en niet ernaast: "geen pas" naast een leeg
        // vinkje zegt niets nieuws, en acht van die regels onder elkaar overschreeuwen
        // de stappen zelf. Zweven levert hem alsnog op als je je afvraagt wát er mist.
        title={stap.toelichting ?? undefined}
        style={{
          color: stap.gedaan ? '#888888' : '#111111',
          textDecoration: stap.gedaan ? 'line-through' : naartoe ? 'underline' : 'none',
          textDecorationColor: '#D6CCBE',
          cursor: naartoe ? 'pointer' : 'default',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {stap.label}
      </span>

      <span style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
        {mail && (
          <button
            type="button"
            onClick={geblokkeerd ? undefined : () => onNavigate(stap, kenteken)}
            disabled={geblokkeerd}
            // Grijs vertelt dat het straks wel kan; de titel zegt waar hij op wacht.
            title={
              stap.blokkade ??
              (mail.naar ? `Mail naar ${mail.naar}` : 'Opent een mail zonder geadresseerde')
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 9px',
              border: `1px solid ${geblokkeerd ? '#E3D9CC' : '#C9BBA8'}`,
              borderRadius: 2,
              background: '#FFFFFF',
              font: 'inherit',
              fontSize: 11.5,
              fontWeight: 700,
              color: geblokkeerd ? '#AAA093' : '#2A6085',
              cursor: geblokkeerd ? 'not-allowed' : 'pointer',
            }}
          >
            ✉ Mail
          </button>
        )}
      </span>
    </div>
  );
}

export function AutoChecklistInhoud({
  checklist,
  onNavigate,
  onToggle,
}: {
  checklist: AutoChecklist;
  onNavigate: OpStapKlik;
  onToggle: (kenteken: string, stap: ChecklistStap) => void;
}) {
  // Alleen op af/niet-af sorteren, en verder de volgorde van het domein aanhouden.
  // Die zet de aanvinkbare stappen vooraan, dus bovenaan staat wat je nú kunt doen.
  // `sort` is stabiel, dus binnen elke helft blijft die volgorde staan.
  const stappen = [...checklist.stappen].sort((a, b) => Number(a.gedaan) - Number(b.gedaan));

  return (
    <div>
      {stappen.map((s) => (
        <StapRegel
          key={s.key}
          stap={s}
          kenteken={checklist.kenteken}
          onNavigate={onNavigate}
          onToggle={(stap) => onToggle(checklist.kenteken, stap)}
        />
      ))}
    </div>
  );
}

/**
 * The same checklist on the car's own page, open by default.
 *
 * No collapse here and no plate in the heading: you are on that car's page, so the
 * list is why you came and the plate is already the biggest thing on screen. No
 * explanation either — the steps say what they want, and a paragraph above them only
 * pushed the first one down the page.
 */
export function AutoChecklistPaneel({
  checklist,
  onNavigate,
  onToggle,
}: {
  checklist: AutoChecklist;
  onNavigate: OpStapKlik;
  onToggle: (kenteken: string, stap: ChecklistStap) => void;
}) {
  return (
    <Panel style={{ padding: '20px 24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Eyebrow color="#B0543B">Nieuwe auto checklist</Eyebrow>
        <Voortgang gevuld={checklist.gedaan} totaal={checklist.totaal} breedte={72} />
      </div>
      <div style={{ marginTop: 14, paddingTop: 6, borderTop: '1px solid #F0E8DC' }}>
        <AutoChecklistInhoud checklist={checklist} onNavigate={onNavigate} onToggle={onToggle} />
      </div>
    </Panel>
  );
}

/** The card title: plate in mono so it reads as a plate, then merk/model. */
export function AutoChecklistTitel({ checklist }: { checklist: AutoChecklist }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
      <span style={{ fontFamily: MONO, fontSize: 12.5 }}>{checklist.kenteken}</span>
      <span
        style={{
          fontWeight: 400,
          color: '#777777',
          fontSize: 12.5,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {checklist.auto}
      </span>
    </span>
  );
}
